import React, { useRef } from 'react';
import useDropdown from 'hooks/useDropdown';
import styled from 'styled-components';
import { RelativeTarget } from 'components/common/RelativeTarget';
import { CEB_COLOR_RGBA } from 'app-constants/CEB_COLOR';
import { StyledDropdownMenu } from 'features/dropdown';

export function FilterDropdown({
  caption,
  children,
  selected,
}: {
  caption: string;
  children: React.ReactNode;
  selected?: boolean;
}) {
  const [dropdown, toggle, onBlur, dropdownRef, buttonRef, setDropdown] =
    useDropdown();

  const buttonWidth = React.useMemo(() => {
    return buttonRef.current?.getBoundingClientRect().width;
  }, [buttonRef]);

  return (
    <StyledFilterDropdown>
      <StyledFilterDropdownButton
        ref={buttonRef as React.RefObject<HTMLButtonElement>}
        onClick={toggle}
        selected={selected}
      >
        {caption}
        <i className="fa fa-caret-down" />
      </StyledFilterDropdownButton>
      {dropdown && (
        <RelativeTarget
          onBlur={onBlur}
          ref={dropdownRef as React.RefObject<HTMLDivElement>}
          target={buttonRef}
          vAnchor="bottom"
          hAnchor="left"
          tabIndex={0}
          style={{
            width: 0,
            outline: 0,
            overflow: 'visible',
          }}
        >
          <StyledFilterDropdownMenu $buttonWidth={buttonWidth}>
            <StyledFilterDropdownChildren>
              {children}
            </StyledFilterDropdownChildren>
          </StyledFilterDropdownMenu>
        </RelativeTarget>
      )}
    </StyledFilterDropdown>
  );
}

const StyledFilterDropdown = styled.div`
  position: relative;
  overflow: visible;
`;

interface StyledFilterDropdownButtonProps {
  selected?: boolean;
}
const StyledFilterDropdownButton = styled.button<StyledFilterDropdownButtonProps>`
  font-size: 13px;
  font-family: var(--FONT_SANS_SERIF);
  font-weight: 900;
  background-color: ${props =>
    props.selected ? '#e3dfda' : 'var(--COLOR_CARARRA)'};
  border-radius: 3px;
  padding: 12px 14px;
  outline: none;

  i {
    margin-left: 6px;
  }
`;

const StyledFilterDropdownMenu = styled(StyledDropdownMenu)<{
  $buttonWidth?: number;
}>`
  transform: translateY(5px);
  width: 300px;
  position: relative;
  &:after {
    left: ${({ $buttonWidth = 12 }) => $buttonWidth - 24}px;
  }
`;

const StyledFilterDropdownChildren = styled.div`
  overflow-y: auto;
  max-height: 50vh;
  padding: 0 11px;
  > label.filter {
    display: flex;
    width: 100%;
    box-sizing: border-box;
    border-radius: 3px;
    box-shadow: 1px 1px 4px 0 rgba(74, 75, 83, 0.08);
    border: solid 1px rgba(52, 52, 52, 0.15);
    margin-bottom: 8px;
    padding: 7px 10px 5px;
    align-items: center;
    input {
      padding: 0 7px;
      font-family: var(--FONT_SANS_SERIF);
      font-size: 15px;
      &::placeholder {
        color: ${CEB_COLOR_RGBA('BLACK', 0.75)};
      }
      &:focus {
        outline: none;
      }
    }
  }
`;
